import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import {
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogCloseIconSx,
  harmonateCancelButtonSx,
} from '../../../theme/harmonateStyles';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getTenantId = () => localStorage.getItem('tenant_id') || '';
const getUserId = () => localStorage.getItem('user_id') || null;
const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch { /* ignore */ }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const formatContent = (content) => {
  if (content == null || content === '') return null;
  if (typeof content === 'object') return JSON.stringify(content, null, 2);
  try {
    return JSON.stringify(JSON.parse(content), null, 2);
  } catch {
    return String(content);
  }
};

const InvoiceContentDialog = ({ open, onClose, invoiceId, invoiceNumber }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formatted, setFormatted] = useState(null);

  useEffect(() => {
    if (!open || !invoiceId) {
      setFormatted(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const loadContent = async () => {
      setLoading(true);
      setError(null);
      setFormatted(null);

      try {
        const header = await apiFetch(`conductor/ap-invoice-headers/${invoiceId}`);
        if (cancelled) return;

        const text = formatContent(header?.content);
        if (!text) {
          setError('No content is stored for this invoice.');
        } else {
          setFormatted(text);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load invoice content');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadContent();

    return () => {
      cancelled = true;
    };
  }, [open, invoiceId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={harmonateDialogPaperProps}>
      <DialogTitle sx={harmonateDialogTitleSx}>
        Content
        {invoiceNumber ? ` — ${invoiceNumber}` : ''}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, minHeight: 120 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!loading && error && (
          <Alert severity="info" sx={{ m: 2 }}>{error}</Alert>
        )}
        {!loading && formatted && (
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              fontSize: '0.75rem',
              lineHeight: 1.5,
              overflow: 'auto',
              maxHeight: '60vh',
              bgcolor: ht.listBg,
            }}
          >
            {formatted}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} variant="outlined" sx={harmonateCancelButtonSx}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceContentDialog;
